/**
 * Clipboard helper with a graceful fallback for non-secure contexts.
 *
 * `navigator.clipboard` is only available on HTTPS / localhost. When the
 * dashboard is served over plain HTTP on a LAN IP (e.g. accessing the dev
 * server from a phone via http://192.168.x.y:3000), the API is undefined
 * and the modern call rejects immediately. We fall back to the legacy
 * `document.execCommand('copy')` path which works everywhere.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  // Modern API: requires a secure context (HTTPS or localhost).
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to legacy path.
    }
  }

  // Legacy fallback using a hidden textarea + execCommand. Deprecated but
  // still supported by every browser we target.
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    // Keep it off-screen but still focusable.
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.top = "0";
    textarea.style.left = "0";
    textarea.style.opacity = "0";
    textarea.style.pointerEvents = "none";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    // iOS Safari needs this range trick to actually select the text.
    textarea.setSelectionRange(0, text.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}
